// ИНВ.2: новый редактор колоды по прототипу из 3 скриншотов.
//
// Структура:
//   - HeroCard: аватар Героя + имя колоды (✎ → модалка) + прогресс + чипы лимитов.
//   - Tabs: КОЛОДА N / КАТАЛОГ N (magenta).
//   - Каталог:
//     - 4 фильтра (тип/редкость/состав-заглушка + toggle «только мои» + сорт).
//     - Сетка 3-колонки. Плитка: буква редкости в углу, count в правом
//       верхнем, иконка-плейсхолдер по типу карты, имя, статы внизу.
//     - Тап на плитку = открыть модалку CardDetailModalV2.
//   - Колода: список карт (или empty state).
//   - BottomBar: СОХРАНИТЬ + «⚠ нужно ещё N карт».
//
// CardDetailModalV2:
//   - Шапка: иконка-силуэт + имя + ×. Подзаголовок ЮНИТ · C (редкость).
//   - Большой плейсхолдер арта [art · 512×768].
//   - Описание способности (все блоки склеены).
//   - Статы: АТК / БРОНЯ / ХП крупно (только для юнитов).
//   - Счётчик В КОЛОДЕ с кнопками − / + (с шагом 1, с учётом всех лимитов).
//   - Keywords (если есть), flavor курсивом.

import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { useTelegram } from '../telegram/useTelegram';
import { useToast } from '../components/Toast';
import { listDecks, createDeck, updateDeck, deleteDeck, getMyCards } from '../api/client';
// @ts-ignore
import { cards as cardsDeus } from '@data/cards-deus.js';
// @ts-ignore
import { cards as cardsSyn } from '@data/cards-syntetics.js';
// @ts-ignore
import { cards as cardsGlb } from '@data/cards-global.js';
// @ts-ignore
import { cards as cardsLiv } from '@data/cards-livlife.js';
// @ts-ignore
import { cards as cardsNeutral } from '@data/cards-neutral.js';
// Герои — отдельные модули, нужны для шапки и модалки Героя.
// @ts-ignore
import { heroes as heroesDeus } from '@data/heroes-deus.js';
// @ts-ignore
import { heroes as heroesSyn } from '@data/heroes-syntetics.js';
// @ts-ignore
import { heroes as heroesGlb } from '@data/heroes-global.js';
// @ts-ignore
import { heroes as heroesLiv } from '@data/heroes-livlife.js';
// @ts-ignore
import { heroes as heroesNeutral } from '@data/heroes-neutral.js';
import { GameCard } from '../components/GameCard';
import type { CardData } from '../lib/cardData';
import { useTr } from '../i18n/useTr';
import { trHeroName, trCardName } from '../i18n/serverStrings';
import styles from './DeckEditorScreen.module.css';

type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
type Rarity = 'common' | 'rare' | 'epic' | 'legendary' | 'special';
type CardType = 'unit' | 'action' | 'mod' | 'influence';

interface Card {
  id: string;
  name_ru: string;
  faction: Faction;
  type: CardType;
  rarity: Rarity;
  atk: number | null;
  armor: number | null;
  hp: number | null;
  keywords: string[];
  exclusive_hero: string | null;
  influence_cost: number | null;
  initiation?: number | null;
  flavor_ru?: string | null;
  ability_text?: { order?: string; trigger?: string; deathrattle?: string; active?: string; aura?: string };
}

const FACTION_CARDS: Record<Faction, Card[]> = {
  deus: cardsDeus as Card[],
  syntetics: cardsSyn as Card[],
  global: cardsGlb as Card[],
  livlife: cardsLiv as Card[],
  neutral: cardsNeutral as Card[]
};
// Призывные токены не должны быть в каталоге.
const NEUTRAL_DECKABLE_CARDS = (cardsNeutral as (Card & { archetype?: string | null })[])
  .filter(c => c.rarity !== 'special' && (c as any).archetype !== 'summon_token');

// Диздок v2.9 §6.2: Лег ≤ 1, Эп ≤ 2, Ред ≤ 6, Об ≤ 24 (= общий лимит).
// Common=24 значит "нет верхней планки сверх общего" — можно собрать колоду
// полностью из обычных. Раньше тут стояло 15 — это была ошибка.
const RARITY_LIMITS = { legendary: 1, epic: 2, rare: 6, common: 24 } as const;
const COPY_LIMITS   = { legendary: 1, epic: 1, rare: 2, common: 3 } as const;

// Цвета редкостей — токены из dis-системы.
const RARITY_COLOR: Record<Rarity, string> = {
  common:    'var(--r-common)',
  rare:      'var(--r-rare)',
  epic:      'var(--r-epic)',
  legendary: 'var(--r-legend)',
  special:   'var(--neon-li)'
};
const RARITY_LETTER: Record<Rarity, string> = {
  common: 'C', rare: 'R', epic: 'E', legendary: 'L', special: 'S'
};
const RARITY_LABEL: Record<Rarity, string> = {
  common: 'Об.', rare: 'Ред.', epic: 'Эпик', legendary: 'Лег.', special: 'Спец.'
};
const RARITY_LABEL_EN: Record<Rarity, string> = {
  common: 'Com.', rare: 'Rare', epic: 'Epic', legendary: 'Leg.', special: 'Spec.'
};
// В единственном числе для ошибок («Лимит Обычной карты в колоде»).
const RARITY_NAME_SG: Record<Rarity, string> = {
  common: 'Обычных', rare: 'Редких', epic: 'Эпических', legendary: 'Легендарных', special: 'Специальных'
};
const RARITY_NAME_SG_EN: Record<Rarity, string> = {
  common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary', special: 'Special'
};
const FACTION_LABEL_CARD: Record<Faction, { name: string; color: string }> = {
  deus:      { name: 'Деус Системс',     color: 'var(--deus)' },
  syntetics: { name: 'Синтетикс Синдикат', color: 'var(--syn)' },
  global:    { name: 'Глобал Секьюрити', color: 'var(--gsec)' },
  livlife:   { name: 'Лив Лайф',         color: 'var(--liv)' },
  neutral:   { name: 'Улица',            color: 'var(--neu)' }
};
const FACTION_LABEL_CARD_EN: Record<Faction, { name: string; color: string }> = {
  deus:      { name: 'Deus Systems',         color: 'var(--deus)' },
  syntetics: { name: 'Synthetics Syndicate', color: 'var(--syn)' },
  global:    { name: 'Global Security',      color: 'var(--gsec)' },
  livlife:   { name: 'Liv Life',             color: 'var(--liv)' },
  neutral:   { name: 'Street',               color: 'var(--neu)' }
};

// Иконка-плейсхолдер по типу карты. Цвет = цвет редкости.
function CardTypeIcon({ type, color, size = 28 }: { type: CardType; color: string; size?: number }) {
  if (type === 'unit') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <circle cx="12" cy="8" r="4"/>
        <path d="M4 21a8 8 0 0 1 16 0"/>
      </svg>
    );
  }
  if (type === 'action') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
        <polygon points="13,2 3,14 11,14 9,22 21,10 13,10"/>
      </svg>
    );
  }
  if (type === 'mod') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/>
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
      <polygon points="12,2 22,12 12,22 2,12"/>
    </svg>
  );
}

// Все блоки ability_text склеены через перенос (для модалки).
function fullAbilityText(c: Card): string {
  const at = c.ability_text || {};
  const parts: string[] = [];
  if (at.order)       parts.push(at.order);
  if (at.active)      parts.push(at.active);
  if (at.trigger)     parts.push(at.trigger);
  if (at.aura)        parts.push(at.aura);
  if (at.deathrattle) parts.push(at.deathrattle);
  return parts.join('\n').trim();
}

// Все Герои в одной мапе (для шапки и модалки в редакторе).
interface HeroData {
  id: string; name_ru: string; faction: Faction;
  atk: number; armor: number; hp: number;
  archetype?: string | null;
  ability_text?: { active?: string; trigger?: string; deathrattle?: string; aura?: string };
  flavor_ru?: string | null;
}
const ALL_HEROES: Record<string, HeroData> = {};
for (const list of [heroesDeus, heroesSyn, heroesGlb, heroesLiv, heroesNeutral]) {
  for (const h of (list as HeroData[])) ALL_HEROES[h.id] = h;
}
function getHero(heroId: string): HeroData | null {
  return ALL_HEROES[heroId] || null;
}

// Параметры фильтрации и сортировки.
type TypeFilter = 'all' | 'unit' | 'action' | 'mod';
type RarityFilter = 'all' | 'common' | 'rare' | 'epic' | 'legendary';
type ComposFilter = 'all' | 'corp' | 'neutral';
type InventoryFilter = 'mine' | 'available' | 'all'; // циклический toggle
type SortMode = 'atk_desc' | 'atk_asc' | 'hp_desc' | 'hp_asc' | 'name';

const INVENTORY_FILTER_LABEL: Record<InventoryFilter, string> = {
  mine:      'ТОЛЬКО МОИ',
  available: 'НЕ В КОЛОДЕ',
  all:       'ВСЕ'
};
const INVENTORY_FILTER_LABEL_EN: Record<InventoryFilter, string> = {
  mine:      'MINE ONLY',
  available: 'NOT IN DECK',
  all:       'ALL'
};
function nextInventoryFilter(v: InventoryFilter): InventoryFilter {
  if (v === 'mine')      return 'available';
  if (v === 'available') return 'all';
  return 'mine';
}

export function DeckEditorScreen() {
  const navigate = useNavigate();
  const { hapticTap, hapticError, hapticSuccess } = useTelegram();
  const toast = useToast(s => s.show);
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const factionLabelCard = isEn ? FACTION_LABEL_CARD_EN : FACTION_LABEL_CARD;
  const rarityLabel = isEn ? RARITY_LABEL_EN : RARITY_LABEL;
  const raritySg = isEn ? RARITY_NAME_SG_EN : RARITY_NAME_SG;
  const invFilterLabel = isEn ? INVENTORY_FILTER_LABEL_EN : INVENTORY_FILTER_LABEL;
  const [searchParams] = useSearchParams();
  const faction = (searchParams.get('faction') as Faction) || 'deus';
  const heroId = searchParams.get('hero') || '';
  const editId = searchParams.get('id') ? parseInt(searchParams.get('id')!, 10) : null;

  const [tab, setTab] = useState<'deck' | 'catalog'>('catalog');
  const [deckCards, setDeckCards] = useState<Map<string, number>>(new Map());
  const [deckName, setDeckName] = useState(tr('Колода', 'Deck'));
  const [nameModal, setNameModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>('all');
  // 'Состав': все / только корпа / только нейтралы.
  const [composFilter, setComposFilter] = useState<ComposFilter>('all');
  // Циклический toggle: Только мои → Не в колоде → Все → ...
  const [invFilter, setInvFilter] = useState<InventoryFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('atk_desc');
  const [saving, setSaving] = useState(false);
  const [ownedCards, setOwnedCards] = useState<Map<string, number> | null>(null);
  const [infoCard, setInfoCard] = useState<Card | null>(null);
  const [heroModalOpen, setHeroModalOpen] = useState(false);

  // Загрузка существующей колоды.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listDecks();
        const d = list.find(x => x.id === editId);
        if (!d || cancelled) return;
        setDeckName(d.name);
        const map = new Map<string, number>();
        for (const e of d.cards as { cardId: string; count: number }[]) {
          map.set(e.cardId, e.count);
        }
        setDeckCards(map);
      } catch (e: any) {
        if (!cancelled) toast(tr(`Не удалось загрузить колоду: ${e?.message || ''}`, `Couldn't load deck: ${e?.message || ''}`), 'red');
      }
    })();
    return () => { cancelled = true; };
  }, [editId, toast, tr]);

  // Инвентарь карт. Перезагружаем при mount + при возврате фокуса
  // на вкладку (случай: ушёл в маркет, продал/купил карту, вернулся).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await getMyCards();
        if (cancelled) return;
        const map = new Map<string, number>();
        for (const it of r.items) map.set(it.cardId, it.count);
        setOwnedCards(map);
      } catch { if (!cancelled) setOwnedCards(null); }
    };
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Базовый пул карт фракции + нейтралы.
  const factionCards = useMemo(() => {
    const own = (FACTION_CARDS[faction] || []).filter(c =>
      c.type !== 'influence' && (c.type as string) !== 'hero'
    );
    if (faction === 'neutral') {
      return NEUTRAL_DECKABLE_CARDS.filter(c =>
        c.type !== 'influence' && (c.type as string) !== 'hero'
      );
    }
    return [...own, ...NEUTRAL_DECKABLE_CARDS];
  }, [faction]);

  // Применяем фильтры.
  const filteredCatalog = useMemo(() => {
    let list = factionCards.filter(c => {
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      if (rarityFilter !== 'all' && c.rarity !== rarityFilter) return false;
      // Этап 56: exclusive_hero — это ВНУТРЕННЯЯ мета-подсказка (балансная), а не
      // игровое ограничение. Не фильтруем каталог по ней: показываем все карты
      // фракции независимо от Героя.
      // Состав: корпа / нейтрал.
      if (composFilter === 'corp' && c.faction === 'neutral') return false;
      if (composFilter === 'neutral' && c.faction !== 'neutral') return false;
      // Инвентарь-фильтр (3-state).
      if (invFilter === 'mine') {
        const has = c.faction === 'neutral'
          ? true
          : (ownedCards?.get(c.id) ?? 0) > 0;
        if (!has) return false;
      } else if (invFilter === 'available') {
        // Те что в инвентаре И ещё можно добавить (count в колоде < min(owned, copyLimit)).
        const owned = c.faction === 'neutral'
          ? Number.MAX_SAFE_INTEGER
          : (ownedCards?.get(c.id) ?? 0);
        if (owned === 0) return false;
        const inDeck = deckCards.get(c.id) || 0;
        const copyLimit = c.type === 'mod' ? 3 : (COPY_LIMITS[c.rarity as keyof typeof COPY_LIMITS] ?? 1);
        if (inDeck >= Math.min(owned, copyLimit)) return false;
      }
      return true;
    });
    // Сортировка.
    list = [...list].sort((a, b) => {
      switch (sortMode) {
        case 'atk_desc': return (b.atk ?? -1) - (a.atk ?? -1) || a.name_ru.localeCompare(b.name_ru);
        case 'atk_asc':  return (a.atk ?? 99) - (b.atk ?? 99) || a.name_ru.localeCompare(b.name_ru);
        case 'hp_desc':  return (b.hp  ?? -1) - (a.hp  ?? -1) || a.name_ru.localeCompare(b.name_ru);
        case 'hp_asc':   return (a.hp  ?? 99) - (b.hp  ?? 99) || a.name_ru.localeCompare(b.name_ru);
        case 'name':     return a.name_ru.localeCompare(b.name_ru);
      }
    });
    return list;
  }, [factionCards, typeFilter, rarityFilter, heroId, invFilter, composFilter, ownedCards, deckCards, sortMode]);

  // Счётчики редкостей в колоде.
  const rarityCounts = useMemo(() => {
    const r = { legendary: 0, epic: 0, rare: 0, common: 0, special: 0 };
    for (const [id, count] of deckCards) {
      const card = factionCards.find(c => c.id === id);
      if (card) r[card.rarity] += count;
    }
    return r;
  }, [deckCards, factionCards]);

  // Этап 56: Моды (rarity 'special') считаются в общий лимит 24 (решение фаундера:
  // «колода = 24 карты в т.ч. Моды, в лимит Обычных»). Раньше special не входил
  // в total → редактор показывал «24/24» при фактических 25 → бой брал фабричную колоду.
  const totalCards = rarityCounts.legendary + rarityCounts.epic + rarityCounts.rare
    + rarityCounts.common + rarityCounts.special;
  const modCount = (faction === 'syntetics')
    ? Array.from(deckCards.entries())
        .filter(([id]) => factionCards.find(c => c.id === id)?.type === 'mod')
        .reduce((a, [, c]) => a + c, 0)
    : 0;

  const canAdd = (card: Card): { ok: boolean; reason?: string } => {
    const current = deckCards.get(card.id) || 0;
    // Моды (Синтетикс) — отдельный пул: до 3 копий каждого, вне квоты редкости юнитов.
    const copyLimit = card.type === 'mod' ? 3 : (COPY_LIMITS[card.rarity as keyof typeof COPY_LIMITS] ?? 1);
    const ownedCount = card.faction === 'neutral'
      ? Number.MAX_SAFE_INTEGER
      : (ownedCards?.get(card.id) ?? (ownedCards === null ? Number.MAX_SAFE_INTEGER : 0));
    if (current >= ownedCount) return { ok: false, reason: tr(`В коллекции только ${ownedCount} копий`, `Only ${ownedCount} copies in collection`) };
    if (current >= copyLimit)  return { ok: false, reason: tr(`Не больше ${copyLimit} копий этой карты в колоде`, `Max ${copyLimit} copies of this card per deck`) };
    const rarityLimit = RARITY_LIMITS[card.rarity as keyof typeof RARITY_LIMITS];
    if (card.type !== 'mod' && rarityLimit !== undefined && rarityCounts[card.rarity as keyof typeof rarityCounts] >= rarityLimit) {
      const sg = raritySg[card.rarity];
      return { ok: false, reason: tr(`В колоде максимум ${rarityLimit} ${sg} карт`, `Max ${rarityLimit} ${sg} cards per deck`) };
    }
    if (totalCards >= 24) return { ok: false, reason: tr('Колода уже 24/24', 'Deck is already 24/24') };
    return { ok: true };
  };

  const addCard = (card: Card) => {
    const check = canAdd(card);
    if (!check.ok) { hapticError(); return; }
    hapticTap();
    setDeckCards(prev => {
      const next = new Map(prev);
      next.set(card.id, (next.get(card.id) || 0) + 1);
      return next;
    });
  };
  const removeCard = (cardId: string) => {
    hapticTap();
    setDeckCards(prev => {
      const next = new Map(prev);
      const cur = next.get(cardId) || 0;
      if (cur <= 1) next.delete(cardId);
      else next.set(cardId, cur - 1);
      return next;
    });
  };

  const deckList = useMemo(() => {
    return Array.from(deckCards.entries())
      .map(([id, count]) => ({ card: factionCards.find(c => c.id === id), count }))
      .filter(x => x.card)
      .sort((a, b) => {
        const order = { legendary: 0, epic: 1, rare: 2, common: 3, special: 4 } as const;
        const ar = order[a.card!.rarity];
        const br = order[b.card!.rarity];
        if (ar !== br) return ar - br;
        return a.card!.name_ru.localeCompare(b.card!.name_ru);
      });
  }, [deckCards, factionCards]);

  const onSave = async () => {
    if (totalCards !== 24 || saving) return;
    const name = deckName.trim() || tr('Колода', 'Deck');
    const cards = Array.from(deckCards.entries()).map(([cardId, count]) => ({ cardId, count }));
    hapticTap();
    setSaving(true);
    try {
      if (editId) {
        await updateDeck(editId, { name, cards });
        hapticSuccess();
        toast(tr('Колода обновлена', 'Deck updated'), 'green');
      } else {
        await createDeck({ name, heroId, faction, cards });
        hapticSuccess();
        toast(tr('Колода сохранена', 'Deck saved'), 'green');
      }
      navigate('/decks');
    } catch (e: any) {
      hapticError();
      const msg = String(e?.message || '');
      if (msg.includes('deck_slots_limit_reached')) {
        toast(tr('Лимит сохранённых колод', 'Deck slots limit reached'), 'amber');
      } else {
        toast(tr(`Не удалось сохранить: ${msg}`, `Couldn't save: ${msg}`), 'red');
      }
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!editId) return;
    if (!window.confirm(tr('Удалить колоду без возврата?', 'Delete deck permanently?'))) return;
    hapticTap();
    try {
      await deleteDeck(editId);
      hapticSuccess();
      toast(tr('Колода удалена', 'Deck deleted'), 'green');
      navigate('/decks');
    } catch (e: any) {
      hapticError();
      toast(tr(`Не удалось удалить: ${e?.message || ''}`, `Couldn't delete: ${e?.message || ''}`), 'red');
    }
  };

  const hero = useMemo(() => getHero(heroId), [heroId]);
  const heroFactionColor = hero ? factionLabelCard[hero.faction].color : 'var(--neon-mg)';

  return (
    <ScreenLayout title={tr('РЕДАКТОР', 'EDITOR')} backTo="/decks">
      {/* === Шапка: Hero + имя + прогресс + чипы === */}
      <div className={styles.heroCard} style={{ boxShadow: `inset 0 0 0 0.5px ${heroFactionColor}` }}>
        <div
          className={styles.heroAvatarBox}
          style={{
            cursor: hero ? 'pointer' : 'default',
            color: heroFactionColor,
            boxShadow: `inset 0 0 0 0.5px ${heroFactionColor}`
          }}
          onClick={() => { if (hero) { hapticTap(); setHeroModalOpen(true); } }}
        >
          <CardTypeIcon type="unit" color="currentColor" size={28} />
        </div>
        <div className={styles.deckNameRow}>
          <span className={styles.deckNameText}>
            {hero ? `${trHeroName(hero.id, hero.name_ru)} · ${deckName}` : deckName}
          </span>
        </div>
        <button className={styles.pencilBtn} onClick={() => { hapticTap(); setNameModal(true); }} title={tr('Переименовать', 'Rename')}>✎</button>

        {hero && (
          <div style={{
            gridColumn: '2 / 4',
            display: 'flex', alignItems: 'center', gap: 12,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10.5, letterSpacing: '0.04em',
            color: 'var(--t-md)', marginTop: -2
          }}>
            <span>
              <span style={{ color: '#ff8e8e' }}>{hero.atk}</span>
              <span style={{ color: 'var(--t-lo)' }}>/</span>
              <span style={{ color: '#ffd56a' }}>{hero.armor}</span>
              <span style={{ color: 'var(--t-lo)' }}>/</span>
              <span style={{ color: '#74e398' }}>{hero.hp}</span>
            </span>
            <span style={{ color: heroFactionColor }}>{factionLabelCard[hero.faction].name}</span>
            <span
              onClick={() => { hapticTap(); setHeroModalOpen(true); }}
              style={{
                marginLeft: 'auto', cursor: 'pointer',
                color: 'var(--t-md)', fontFamily: 'Chakra Petch, sans-serif',
                fontSize: 11, fontWeight: 700,
                padding: '2px 6px',
                border: '0.5px solid var(--line)'
              }}
              title={tr('Подробнее о Герое', 'Hero details')}
            >ⓘ</span>
          </div>
        )}

        <div className={styles.progressRow}>
          <span className={styles.progressNum}>
            {totalCards}
            <span className={styles.progressNumDim}> / 24</span>
          </span>
          <div className={styles.progressBarBg}>
            <div className={styles.progressBarFill} style={{ width: `${Math.min(100, totalCards / 24 * 100)}%` }} />
          </div>
        </div>

        <div className={styles.limitChipsRow}>
          <ChipL letter="L" current={rarityCounts.legendary} max={RARITY_LIMITS.legendary} className={styles.legChip} />
          <ChipL letter="E" current={rarityCounts.epic}      max={RARITY_LIMITS.epic}      className={styles.epicChip} />
          <ChipL letter="R" current={rarityCounts.rare}      max={RARITY_LIMITS.rare}      className={styles.rareChip} />
          <ChipL letter="C" current={rarityCounts.common}    max={RARITY_LIMITS.common}    className={styles.commonChip} />
          <ChipL letter="M" current={modCount}               max={Infinity} />
        </div>
      </div>

      {/* === Вкладки === */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'deck' ? styles.tabActive : ''}`}
          onClick={() => setTab('deck')}
        >
          {tr('КОЛОДА', 'DECK')}<span className={styles.tabCount}>{totalCards}</span>
        </button>
        <button
          className={`${styles.tab} ${tab === 'catalog' ? styles.tabActive : ''}`}
          onClick={() => setTab('catalog')}
        >
          {tr('КАТАЛОГ', 'CATALOG')}<span className={styles.tabCount}>{filteredCatalog.length}</span>
        </button>
      </div>

      {tab === 'catalog' && (
        <>
          <div className={styles.filtersWrap}>
            <div className={styles.filtersRow}>
              <Dropdown
                label={tr('ТИП', 'TYPE')}
                value={typeFilter}
                items={[
                  { v: 'all',    label: tr('ВСЕ', 'ALL') },
                  { v: 'unit',   label: tr('ЮНИТЫ', 'UNITS') },
                  { v: 'action', label: tr('ДЕЙСТВИЯ', 'ACTIONS') },
                  ...(faction === 'syntetics' ? [{ v: 'mod', label: tr('МОДЫ', 'MODS') } as const] : [])
                ]}
                onChange={(v) => setTypeFilter(v as TypeFilter)}
              />
              <Dropdown
                label={tr('РЕДКОСТЬ', 'RARITY')}
                value={rarityFilter}
                items={[
                  { v: 'all',       label: tr('ВСЕ', 'ALL') },
                  { v: 'common',    label: tr('Обычные', 'Common'),       color: RARITY_COLOR.common },
                  { v: 'rare',      label: tr('Редкие', 'Rare'),          color: RARITY_COLOR.rare },
                  { v: 'epic',      label: tr('Эпические', 'Epic'),       color: RARITY_COLOR.epic },
                  { v: 'legendary', label: tr('Легендарные', 'Legendary'), color: RARITY_COLOR.legendary }
                ]}
                onChange={(v) => setRarityFilter(v as RarityFilter)}
              />
              <Dropdown
                label={tr('СОСТАВ', 'COMPO')}
                value={composFilter}
                items={[
                  { v: 'all',     label: tr('ВСЕ', 'ALL') },
                  { v: 'corp',    label: hero ? factionLabelCard[hero.faction].name : tr('Корпа', 'Corp'),
                    color: hero ? factionLabelCard[hero.faction].color : undefined },
                  { v: 'neutral', label: tr('Нейтрал', 'Neutral'),  color: 'var(--neu-2)' }
                ]}
                onChange={(v) => setComposFilter(v as ComposFilter)}
              />
            </div>
            <div className={styles.filtersRow}>
              <button
                className={`${styles.filterToggle} ${invFilter !== 'all' ? styles.filterToggleOn : ''}`}
                onClick={() => { hapticTap(); setInvFilter(v => nextInventoryFilter(v)); }}
                title={tr(
                  'Тап циклически: Только мои → Не в колоде → Все',
                  'Tap to cycle: Mine only → Not in deck → All'
                )}
              >
                {invFilterLabel[invFilter]}
              </button>
              <Dropdown
                label={tr('СОРТ.', 'SORT')}
                value={sortMode}
                items={[
                  { v: 'atk_desc', label: tr('АТК ↓', 'ATK ↓') },
                  { v: 'atk_asc',  label: tr('АТК ↑', 'ATK ↑') },
                  { v: 'hp_desc',  label: 'HP ↓' },
                  { v: 'hp_asc',   label: 'HP ↑' },
                  { v: 'name',     label: tr('ИМЯ', 'NAME') }
                ]}
                onChange={(v) => setSortMode(v as SortMode)}
              />
            </div>
          </div>

          <div className={styles.catalogGrid}>
            {filteredCatalog.map(c => {
              const inDeck = deckCards.get(c.id) || 0;
              const ownedCount = c.faction === 'neutral'
                ? Number.MAX_SAFE_INTEGER
                : (ownedCards?.get(c.id) ?? 0);
              const hasInInv = ownedCount > 0;
              const isMissing = !hasInInv;
              const copyLimit = COPY_LIMITS[c.rarity as keyof typeof COPY_LIMITS] ?? 1;
              const cornerMax = c.faction === 'neutral' ? '∞' : Math.min(copyLimit, ownedCount);
              const color = RARITY_COLOR[c.rarity];

              return (
                <div
                  key={c.id}
                  onClick={() => { hapticTap(); setInfoCard(c); }}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  <GameCard
                    card={c as unknown as CardData}
                    variant="compact"
                    dim={isMissing}
                    cornerCount={`${inDeck}/${cornerMax}`}
                  />
                  {isMissing && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-10deg)',
                      background: 'rgba(0,0,0,0.75)',
                      color: '#ff7a7a',
                      padding: '4px 10px',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      border: '1px solid #ff7a7a',
                      borderRadius: 2,
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                      textShadow: '0 0 6px rgba(0,0,0,0.9)'
                    }}>
                      {tr('НЕТ В КОЛЛЕКЦИИ', 'NOT OWNED')}
                    </div>
                  )}
                </div>
              );
            })}
            {filteredCatalog.length === 0 && (
              <div className={styles.empty}>
                {invFilter !== 'all' ? (
                  <>
                    {tr('Карт по фильтру нет.', 'No cards match filters.')}
                    <br />
                    <button
                      onClick={() => setInvFilter('all')}
                      style={{ marginTop: 8, padding: '6px 12px', background: 'var(--neon-cy)', color: '#000', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                    >
                      {tr('Сбросить фильтр инвентаря', 'Reset inventory filter')}
                    </button>
                  </>
                ) : tr('Карт по фильтру нет', 'No cards match filters')}
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'deck' && (
        <>
          {deckList.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>► {tr('Колода пуста.', 'Deck is empty.')}</div>
              <div className={styles.emptyHint}>
                {isEn ? (
                  <>Go to the <strong style={{ color: 'var(--neon-cy)' }}>CATALOG</strong> tab and add cards.</>
                ) : (
                  <>Перейди на вкладку <strong style={{ color: 'var(--neon-cy)' }}>КАТАЛОГ</strong> и добавь карты.</>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.deckList}>
              {deckList.map(({ card, count }) => {
                const color = RARITY_COLOR[card!.rarity];
                return (
                  <div
                    key={card!.id}
                    className={`${styles.deckRow} cut-s`}
                    style={{ boxShadow: `inset 0 0 0 0.5px ${color}` }}
                  >
                    <span className={styles.deckCount}>×{count}</span>
                    <div>
                      <div className={styles.deckRowName}>{trCardName(card!.id, card!.name_ru)}</div>
                      {card!.type === 'unit' && (
                        <div className={styles.deckRowStats}>
                          {rarityLabel[card!.rarity]} · {card!.atk}/{card!.armor}/{card!.hp}
                        </div>
                      )}
                    </div>
                    <span className={styles.deckRowInfo} onClick={() => setInfoCard(card!)}>ⓘ</span>
                    <span
                      onClick={() => removeCard(card!.id)}
                      style={{ color: '#ff7a7a', fontSize: 16, cursor: 'pointer', padding: 4 }}
                    >×</span>
                  </div>
                );
              })}
            </div>
          )}
          {editId && deckList.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                onClick={onDelete}
                style={{
                  fontSize: 10, letterSpacing: '0.08em',
                  color: '#ff7a7a', background: 'transparent',
                  border: '0.5px solid #ff7a7a',
                  padding: '4px 10px', cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >{tr('УДАЛИТЬ КОЛОДУ', 'DELETE DECK')}</button>
            </div>
          )}
        </>
      )}

      <div className={styles.bottomBar}>
        <button className={styles.saveBtn} disabled={totalCards !== 24 || saving} onClick={onSave}>
          {saving ? '…' : tr('СОХРАНИТЬ', 'SAVE')}
        </button>
        {totalCards !== 24 && (
          <div className={styles.saveWarning}>
            ⚠ {tr(`нужно ещё ${24 - totalCards} карт`, `need ${24 - totalCards} more card(s)`)}
          </div>
        )}
      </div>

      {/* Модалка с инфой о карте */}
      {infoCard && (
        <CardDetailModalV2
          card={infoCard}
          inDeck={deckCards.get(infoCard.id) || 0}
          canAdd={canAdd(infoCard)}
          onAdd={() => addCard(infoCard)}
          onRemove={() => removeCard(infoCard.id)}
          onClose={() => setInfoCard(null)}
        />
      )}

      {/* Модалка переименования колоды */}
      {nameModal && (
        <NameEditModal
          initial={deckName}
          onClose={() => setNameModal(false)}
          onSave={(v) => { setDeckName(v); setNameModal(false); }}
        />
      )}

      {/* Модалка Героя */}
      {heroModalOpen && hero && (
        <HeroDetailModal hero={hero} onClose={() => setHeroModalOpen(false)} />
      )}
    </ScreenLayout>
  );
}

// === Сабкомпоненты ===

function ChipL({ letter, current, max, className }: { letter: string; current: number; max: number; className?: string }) {
  const full = current >= max && max !== Infinity;
  return (
    <span className={`${styles.limitChip} ${className || ''} ${full ? styles.limitFull : ''}`}>
      {letter} <strong>{current}</strong>/{max === Infinity ? '∞' : max}
    </span>
  );
}

function Dropdown<T extends string>({ label, value, items, onChange }: {
  label: string;
  value: T;
  items: readonly { v: T; label: string; color?: string }[];
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const cur = items.find(it => it.v === value) || items[0];
  const isActive = value !== items[0].v;
  return (
    <div className={styles.dropdownWrap} ref={ref}>
      <button
        className={`${styles.filterDropdown} ${isActive ? styles.filterDropdownActive : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span>
          {label}: <span style={{ color: cur.color || 'inherit' }}>{cur.label}</span>
        </span>
      </button>
      {open && (
        <div className={styles.dropdownPanel}>
          {items.map(it => (
            <button
              key={it.v}
              className={`${styles.dropdownItem} ${it.v === value ? styles.dropdownItemActive : ''}`}
              onClick={() => { onChange(it.v); setOpen(false); }}
              style={it.color ? { color: it.color } : undefined}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ИНВ.2: модалка Героя — статы + способности + flavor.
function HeroDetailModal({ hero, onClose }: { hero: HeroData; onClose: () => void }) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const fac = isEn ? FACTION_LABEL_CARD_EN[hero.faction] : FACTION_LABEL_CARD[hero.faction];
  const at = hero.ability_text || {};
  const blocks: { label: string; text: string; color: string }[] = [];
  if (at.active)      blocks.push({ label: tr('АКТИВКА', 'ACTIVE'),         text: at.active,      color: 'var(--neon-cy)' });
  if (at.trigger)     blocks.push({ label: tr('ТРИГГЕР', 'TRIGGER'),        text: at.trigger,     color: 'var(--r-epic)' });
  if (at.aura)        blocks.push({ label: tr('АУРА', 'AURA'),              text: at.aura,        color: 'var(--neon-li)' });
  if (at.deathrattle) blocks.push({ label: tr('ПОСМЕРТИЕ', 'DEATHRATTLE'),  text: at.deathrattle, color: 'var(--r-legend)' });
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div
        className={styles.cdmBox}
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: `inset 0 0 0 0.5px ${fac.color}` }}
      >
        <div className={styles.cdmHeader}>
          <div className={styles.cdmHeroIcon} style={{ color: fac.color, boxShadow: `inset 0 0 0 0.5px ${fac.color}` }}>
            <CardTypeIcon type="unit" color="currentColor" size={20} />
          </div>
          <div className={styles.cdmTitleCol}>
            <div className={styles.cdmTitle}>{trHeroName(hero.id, hero.name_ru)}</div>
            <div className={styles.cdmSub}>
              {tr('ГЕРОЙ', 'HERO')} · <span style={{ color: fac.color }}>{fac.name}</span>
              {hero.archetype && <> · {hero.archetype}</>}
            </div>
          </div>
          <button className={styles.cdmClose} onClick={onClose}>×</button>
        </div>

        <div className={styles.cdmStatsRow}>
          <div className={styles.cdmStat}>
            <div className={styles.cdmStatVal} style={{ color: '#ff8e8e' }}>{hero.atk}</div>
            <div className={styles.cdmStatLbl}>⚔ {tr('АТК', 'ATK')}</div>
          </div>
          <div className={styles.cdmStat}>
            <div className={styles.cdmStatVal} style={{ color: '#ffd56a' }}>{hero.armor}</div>
            <div className={styles.cdmStatLbl}>◊ {tr('БРОНЯ', 'ARMOR')}</div>
          </div>
          <div className={styles.cdmStat}>
            <div className={styles.cdmStatVal} style={{ color: '#74e398' }}>{hero.hp}</div>
            <div className={styles.cdmStatLbl}>♥ {tr('ХП', 'HP')}</div>
          </div>
        </div>

        {blocks.map((b, i) => (
          <div key={i} style={{
            padding: '8px 12px',
            background: 'var(--bg-0)',
            boxShadow: `inset 0 0 0 0.5px ${b.color}`
          }}>
            <div className="cd" style={{
              fontSize: 9, letterSpacing: '0.12em', color: b.color, marginBottom: 4
            }}>{b.label}</div>
            <div style={{ fontSize: 12, color: 'var(--t-hi)', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
              {b.text}
            </div>
          </div>
        ))}

        {hero.flavor_ru && (
          <div className={styles.cdmFlavor}>«{hero.flavor_ru}»</div>
        )}
      </div>
    </div>
  );
}

function CardDetailModalV2({ card, inDeck, canAdd, onAdd, onRemove, onClose }: {
  card: Card;
  inDeck: number;
  canAdd: { ok: boolean; reason?: string };
  onAdd: () => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const tr = useTr();
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div
        className={styles.cdmBox}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
          maxWidth: 340
        }}
      >
        {/* Close button (вне карты) */}
        <button
          className={styles.cdmClose}
          onClick={onClose}
          style={{
            position: 'absolute',
            top: -8, right: -8,
            background: 'var(--bg-2)',
            border: '1px solid var(--line)',
            width: 32, height: 32,
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2
          }}
        >×</button>

        {/* Сама карта в full-режиме (единый дизайн со сценой ОТК.1). */}
        <div style={{ width: 320, margin: '0 auto' }}>
          <GameCard
            card={card as unknown as CardData}
            variant="full"
            status={null}
          />
        </div>

        {/* Этап 56: плашка «Эксклюзив Героя» убрана — это внутренняя мета-инфа,
            юзеру видеть не нужно. */}

        {/* Счётчик в колоде под картой. */}
        <div className={styles.cdmCounter} style={{ marginTop: 8 }}>
          <span className={styles.cdmCounterLbl}>{tr('В КОЛОДЕ', 'IN DECK')}</span>
          <button
            className={styles.cdmCounterBtn}
            disabled={inDeck === 0}
            onClick={onRemove}
          >−</button>
          <span className={styles.cdmCounterVal}>{inDeck}</span>
          <button
            className={styles.cdmCounterBtn}
            disabled={!canAdd.ok}
            onClick={onAdd}
            title={canAdd.reason}
          >+</button>
        </div>

        {!canAdd.ok && canAdd.reason && (
          <div style={{ fontSize: 10, color: 'var(--neon-am)', textAlign: 'center', letterSpacing: '0.04em', marginTop: 4 }}>
            ⚠ {canAdd.reason}
          </div>
        )}

        {card.flavor_ru && (
          <div className={styles.cdmFlavor} style={{ marginTop: 6 }}>«{card.flavor_ru}»</div>
        )}
      </div>
    </div>
  );
}

function NameEditModal({ initial, onClose, onSave }: {
  initial: string;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const tr = useTr();
  const [val, setVal] = useState(initial);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div
        className={styles.cdmBox}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 320, gap: 12 }}
      >
        <div className="cd" style={{
          color: 'var(--neon-cy)', fontSize: 12, letterSpacing: '0.14em',
          textAlign: 'center'
        }}>
          ► {tr('НАЗВАНИЕ КОЛОДЫ', 'DECK NAME')}
        </div>
        <input
          ref={inputRef}
          className={styles.nameModalInput}
          value={val}
          maxLength={64}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(val.trim() || initial); if (e.key === 'Escape') onClose(); }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <button
            onClick={onClose}
            style={{
              padding: 10, background: 'transparent', color: 'var(--t-md)',
              border: '0.5px solid var(--t-md)',
              fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '0.08em'
            }}
          >{tr('ОТМЕНА', 'CANCEL')}</button>
          <button
            onClick={() => onSave(val.trim() || initial)}
            style={{
              padding: 10, background: 'var(--neon-cy)', color: 'var(--bg)',
              border: 'none',
              fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '0.08em', fontWeight: 700
            }}
          >{tr('СОХРАНИТЬ', 'SAVE')}</button>
        </div>
      </div>
    </div>
  );
}
